import React, { useEffect, useState } from "react";
import { AiFillPlusCircle } from "react-icons/ai";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import {
  getAllRoles,
  createRole,
  deleteRole,
} from "../../services/ApiService";
import { Button, Form, Input, Modal, message, Table, Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../GlobalFunction";
import "../Fields/FieldList.css";

const RoleList = () => {
  const navigate = useNavigate();
  const canCreate = hasPermission("roles", "create");
  const canUpdate = hasPermission("roles", "update");
  const canDelete = hasPermission("roles", "delete");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await getAllRoles();
      if (res.responseCode === 200) {
        setRoles(res.data || []);
      } else {
        message.error(res.responseMessage || "Failed to fetch roles");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      message.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleAdd = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const res = await createRole({ role_name: values.role_name });
      if (res.responseCode === 200) {
        message.success("Role created successfully");
        setModalVisible(false);
        fetchRoles();
      } else {
        message.error(res.responseMessage || "Failed to create role");
      }
    } catch (error) {
      if (error?.errorFields) return; // form validation error, already shown inline
      console.error("Error creating role:", error);
      message.error("Failed to create role");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteRole(id);
      if (res.responseCode === 200) {
        message.success("Role deleted successfully");
        fetchRoles();
      } else {
        message.error(res.responseMessage || "Failed to delete role");
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      message.error("Failed to delete role");
    }
  };

  const columns = [
    {
      title: "Sr. No",
      key: "sr",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Role Name",
      dataIndex: "role_name",
      key: "role_name",
    },
    {
      title: "Modules with Access",
      key: "modules",
      render: (_, record) =>
        Object.entries(record.permissions || {}).filter(
          ([, actions]) => actions.length > 0
        ).length,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          {canUpdate && (
            <Button
              type="primary"
              onClick={() =>
                navigate(`/edit-role-permissions?id=${record.id}`)
              }
              style={{ marginRight: "0.5rem" }}
            >
              Edit Permissions
            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title="Delete this role?"
              description="Users still assigned to it must be reassigned first."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="Cancel"
            >
              <Button color="danger">Delete</Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="main-box">
      <Sidebar activeId={11} />
      <main className="main-content">
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
          <Header title="Roles & Permissions" />
          <div
            style={{
              flex: 1,
              padding: "1.5rem",
              boxSizing: "border-box",
              overflowY: "auto",
            }}
          >
            {canCreate && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div className="field-box" onClick={handleAdd}>
                  <AiFillPlusCircle color="#534ECC" />
                  <span style={{ color: "#344767", fontSize: "1rem", fontWeight: 700 }}>
                    {" "}Create Role{" "}
                  </span>
                </div>
              </div>
            )}
            <p
              style={{
                textAlign: "center",
                fontFamily: "Inter",
                fontSize: "0.8rem",
                color: "#7B809A",
              }}
            >
              Create a role, then set which modules it can view, create,
              update, or delete. Assign the role to a user from the Users page.
            </p>
            <Table
              columns={columns}
              dataSource={roles}
              rowKey="id"
              loading={loading}
              pagination={false}
              scroll={{ x: "max-content" }}
              style={{
                marginTop: "1rem",
                fontSize: "0.8rem",
                borderRadius: "1.5rem",
                background: "#FFF",
                boxShadow: "0px 1.72px 5.161px 0px rgba(0, 0, 0, 0.25)",
              }}
              className="custom-scrollbar"
            />
          </div>
        </div>
      </main>

      <Modal
        title={<span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>Create Role</span>}
        centered
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="role_name"
            label="Role Name"
            rules={[{ required: true, message: "Role name is required" }]}
          >
            <Input placeholder="e.g. Reviewer" className="input-box" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleList;
